// import React from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import SectionDivider from './_sectionDivider';
import SustainabilityScoreCircle from 'kosmos/components/SustainabilityScoreCircle';
// import Barchart from 'components/Barchart';
import SubSection from './_subSection';
import { date2str } from 'utils/date';
import { useTranslation } from 'react-i18next';

const PREFIX = '_indicativeRegulatoryReport';

const classes = {
  chartContainer: `${PREFIX}-chartContainer`,
  barContainer: `${PREFIX}-barContainer`,
  chart: `${PREFIX}-chart`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  [`& .${classes.chartContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
  },
  [`& .${classes.barContainer}`]: {
    height: 500,
  },
  [`& .${classes.chart}`]: {
    flex: 1,
    textAlign: 'center',
  },
}));

interface IProps {
  name: string;
  address: string;
  companyRegistrationNumber: string;
  companyRegistrationNumberDescriptor: string;
  overallScore: number;
  kosmosEsgEnvironmentScore?: number;
  kosmosEsgSocialScore?: number;
  kosmosEsgGovernanceScore?: number;
  reportId: string;
  reportDate: string;
}

export default function IndicativeRegulatoryReport(props: IProps): JSX.Element {
  const {
    name,
    address,
    companyRegistrationNumber,
    companyRegistrationNumberDescriptor,
    reportId,
    reportDate,
    overallScore,
  } = props;
  const { t } = useTranslation('default');
  const renderProperty = (label: string, value: string | number) => {
    if (value) {
      return (
        <div style={{ marginTop: 25 }}>
          <label>{label}</label>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            {value}
          </Typography>
        </div>
      );
    }
  };

  const renderDimensionScore = (score: number, title: string) => {
    return (
      <div className={classes.chart}>
        <SustainabilityScoreCircle
          prop="score"
          score={score}
          width={200}
          height={200}
          size="small"
        />
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
      </div>
    );
  };

  const renderSDGScores = () => {
    const {
      kosmosEsgEnvironmentScore = 0,
      kosmosEsgSocialScore = 0,
      kosmosEsgGovernanceScore = 0,
    } = props;
    return (
      <SubSection>
        <div className={classes.chartContainer}>
          {renderDimensionScore(kosmosEsgEnvironmentScore, t('environmental'))}
          {renderDimensionScore(kosmosEsgSocialScore, t('social'))}
          {renderDimensionScore(kosmosEsgGovernanceScore, t('governance'))}
        </div>
      </SubSection>
    );
  };

  // const renderIndustryBenchmarking = () => {
  //   return (
  //     <SubSection isBold title="Industry Benchmarking">
  //       <>
  //         <Typography
  //           variant="h6"
  //           gutterBottom
  //           sx={{ fontSize: '16px' }}
  //         >
  //           Benchmarking against rolling average of all companies found in your industry.
  //         </Typography>
  //         <Typography
  //           variant="h6"
  //           gutterBottom
  //           sx={{ fontSize: '16px' }}
  //         >
  //           Industry: industry name
  //         </Typography>
  //         <div className={classes.barContainer}>
  //           <Barchart />
  //         </div>
  //       </>
  //     </SubSection>
  //   );
  // };
  // console.log(renderIndustryBenchmarking);

  return (
    <Root>
      <SubSection isBold title={name}>
        <>
          {renderProperty(t('registered-office-address'), address)}
          {renderProperty(t('score'), overallScore)}
          {companyRegistrationNumber &&
            renderProperty(t('company-number'), companyRegistrationNumber)}
          {companyRegistrationNumber &&
            companyRegistrationNumberDescriptor &&
            renderProperty(t('company-registration-type'), companyRegistrationNumberDescriptor)}
          {renderProperty(t('report-number'), reportId)}
          {renderProperty(t('report-date'), date2str(reportDate, 'DD-MMM-YYYY'))}
        </>
      </SubSection>
      <SectionDivider color="#dddddd" id="esg-ratings" />
      <SubSection isBold title={t('overall-score')}>
        <div className={classes.chartContainer}>
          <div className={classes.chart}>
            <SustainabilityScoreCircle score={overallScore} width={350} height={350} />
          </div>
        </div>
      </SubSection>
      <SectionDivider color="#dddddd" />
      {renderSDGScores()}
    </Root>
  );
}
